import {
    Box,
    Button,
    Chip,
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ListItemText,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
} from '@material-ui/core';
import agent from 'api/agent';
import { Printer, PrinterInfoStatus, PrinterServer, ReceiptType, TestPrintRequest } from 'api/models';
import React, { useEffect, useState } from 'react';
import { formatDateTimeGermany } from 'helpers/datetimeHelper';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import commonStyles from 'config/commonStyles';
import i18n from 'i18n-js';
import { BasicThreeDotsMenu, PopupConfirmation } from 'components/shared';
import DeleteIcon from '@material-ui/icons/Delete';
import PrintIcon from '@material-ui/icons/Print';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import RefreshIcon from '@material-ui/icons/Refresh';
import { images } from 'assets';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        ...commonStyles,
        tableBody: {
            height: '100%',
            overflowY: 'auto',
            maxHeight: 400,
        },
        paperKeyContainer: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
        },
        apiKeyText: {
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            flex: 1,
        },
        dialogContentText: {
            color: theme.palette.text.primary,
        },
        dialogActions: {
            paddingRight: theme.spacing(3),
            marginBottom: 0
        },
        titleRoot: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        cancelButton: {
            color: theme.palette.primary.main,
            letterSpacing: 1.25,
        },
    })
);

interface Props {
    restaurantId: string;
}

export default function AdminAdvancedSettings(props: Props) {
    const { restaurantId } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const [servers, setServers] = useState<PrinterServer[]>([]);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [key, setKey] = useState<string>('');
    const [dialogTitle, setDialogTitle] = useState<string>('');
    const [dialogBody, setDialogBody] = useState<string>('');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
    const [selectedServer, setSelectedServer] = useState<PrinterServer | null>(null);

    function getDate(date?: string) {
        if (!date) return 'N/A';

        return formatDateTimeGermany(new Date(date));
    }

    function createNewPrinterServer() {
        setDialogTitle(i18n.t('messages.serverAssigned'));
        setDialogBody(i18n.t('warnings.copyKeyAndStoreSecurely'));
        agent.PrinterServers.create(restaurantId).then((res) => {
            setServers([...servers, res]);
            setKey(res.apiKey);
            setDialogOpen(true);
        });
    }

    function copyToClipboard(text: string): boolean {
        if (!text) return false;

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text);
            return true;
        }

        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand('copy');
            return true;
        } catch {
            return false;
        } finally {
            document.body.removeChild(textArea);
        }
    }

    function handleCopyKey() {
        const isCopied = copyToClipboard(key);
        if (isCopied) {
            dispatch(globalStore.actionCreators.showToaster('success', i18n.t('common.copied')));
        } else {
            dispatch(
                globalStore.actionCreators.showToaster(
                    'error',
                    i18n.t('warnings.cannotCopySelectText')
                )
            );
        }
    }

    function handleCopyAndClose() {
        setDialogOpen(false);
        const blob = new Blob([key], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `print_server_key.txt`;
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setKey('');
    }

    function handleClose() {
        setDialogOpen(false);
        setKey('');
    }

    function handleTestPrintClick(type: ReceiptType, server: PrinterServer, printer: Printer) {
        const model: TestPrintRequest = {
            printerIp: printer.address,
            receiptType: type,
        };
        agent.PrinterServers.testPrint(server.id, model)
            .then((res) => {
                dispatch(globalStore.actionCreators.showToaster('success', i18n.t('orders.done')));
            })
            .catch((err) => {
                console.error(err);
                dispatch(
                    globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong'))
                );
            });
    }

    function prepareServerMenuItems(server: PrinterServer) {
        let result = [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => onGenerateKeyClick(server)}
            >
                <RefreshIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.generateApiKey')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleRetrieveKey(server)}
            >
                <VpnKeyIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.retrieveApiKey')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => {
                    agent.PrinterServers.delete(server.id).then((res) => {
                        const newServers = servers.filter((x) => x.id !== res.id);
                        setServers(newServers);
                    });
                }}
            >
                <DeleteIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('common.delete')} />
            </div>,
        ];

        return result;
    }

    function handleRetrieveKey(server: PrinterServer) {
        setDialogTitle(i18n.t('common.apiKeyTitle'));
        setDialogBody(i18n.t('warnings.copyKeyAndStoreSecurely'));
        agent.PrinterServers.retrieveApiKey(server.id)
            .then(res => {
                setKey(res.apiKey);
                setDialogOpen(true);
            })
            .catch((err) => {
                dispatch(
                    globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong'))
                );
            });
    }

    function handleRegenerateKey(server: PrinterServer) {
        setSelectedServer(null);
        setConfirmDialogOpen(false);
        setDialogTitle(i18n.t('common.newApiKeyTitle'));
        setDialogBody(i18n.t('warnings.copyKeyAndStoreSecurely'));
        agent.PrinterServers.regenerateApiKey(server.id)
            .then(res => {
                setKey(res.apiKey);
                setDialogOpen(true);
            })
            .catch((err) => {
                dispatch(
                    globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong'))
                );
            });
    }

    function onGenerateKeyClick(server: PrinterServer) {
        setSelectedServer(server);
        setConfirmDialogOpen(true);
    }

    function preparePrinterMenuItems(server: PrinterServer, printer: Printer) {
        let result = [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleTestPrintClick(ReceiptType.Receipt, server, printer)}
            >
                <PrintIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.testPrintReceipt')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleTestPrintClick(ReceiptType.WorkareaTicket, server, printer)}
            >
                <PrintIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.testPrintKitchenTicket')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleTestPrintClick(ReceiptType.NextCourse, server, printer)}
            >
                <PrintIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={'Test print next course'} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleTestPrintClick(ReceiptType.OrdersOverview, server, printer)}
            >
                <PrintIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={'Test print order overview'} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleTestPrintClick(ReceiptType.InvoiceReceipt, server, printer)}
            >
                <PrintIcon color="disabled" className={classes.threeDotsItemIcon} />
                <ListItemText primary={'Test print invoice receipt'} />
            </div>,
        ];

        return result;
    }

    function getServers() {
        agent.PrinterServers.getList(restaurantId).then((res) => {
            setServers(res);
        });
    }

    useEffect(() => {
        if (!restaurantId) return;
        getServers();
    }, [restaurantId]);

    useEffect(() => {
        const timeout = setInterval(() => {
            if (!restaurantId) return;
            getServers();
        }, 5000);

        return () => clearInterval(timeout);
    }, []);

    return (
        <>
            <Box m={3}>
                <Typography variant={'h6'} gutterBottom>
                    {i18n.t('common.printerServers')}
                </Typography>
                <Button
                    className={classes.mTop10}
                    variant='outlined'
                    color='primary'
                    onClick={createNewPrinterServer}>
                        {i18n.t('button.assignNewPrinterServer')}
                </Button>
            </Box>
            <TableContainer className={classes.tableBody}>
                <Table stickyHeader className={classes.table} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{i18n.t('common.status')}</TableCell>
                            <TableCell>{i18n.t('common.lastSeen')}</TableCell>
                            <TableCell>{i18n.t('common.osVersion')}</TableCell>
                            <TableCell>
                                {i18n.t('common.printers')}
                            </TableCell>
                            <TableCell align="right" style={{ paddingRight: 8 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {servers.length === 0 && (
                            <Box m={3}>
                                <Typography variant="body2" color="textSecondary">
                                    {i18n.t('common.noServersAttached')}
                                </Typography>
                            </Box>
                        )}
                        {servers.length > 0 &&
                            servers.map((server, i) => (
                                <React.Fragment key={server.id}>
                                    <TableRow hover>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>
                                            {server.isOnline ? i18n.t('common.online') : i18n.t('common.offline')}
                                        </TableCell>
                                        <TableCell>{getDate(server.lastSeenUtc)}</TableCell>
                                        <TableCell>{server.osVersion}</TableCell>
                                        <TableCell>{server.printers.length}</TableCell>
                                        <TableCell align="right">
                                            <BasicThreeDotsMenu items={prepareServerMenuItems(server)} />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow hover={true}>
                                        <TableCell colSpan={1}></TableCell>
                                        <TableCell colSpan={1}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                {i18n.t('common.printers')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell colSpan={4} padding={'none'}>
                                            <Box m={2}>
                                                <Table size="small">
                                                    {server.printers.length > 0 && (
                                                        <TableHead>
                                                            <TableRow hover={true}>
                                                                <TableCell>{i18n.t('common.printerIPAddress')}</TableCell>
                                                                <TableCell>{i18n.t('common.printerModel')}</TableCell>
                                                                <TableCell>{i18n.t('common.status')}</TableCell>
                                                                <TableCell>{i18n.t('restaurant.workAreas')}</TableCell>
                                                                <TableCell align="right" />
                                                            </TableRow>
                                                        </TableHead>
                                                    )}
                                                    <TableBody>
                                                        {server.printers.length === 0 && (
                                                            <TableCell>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                >
                                                                    {i18n.t('common.noPrintersAttached')}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        {server.printers.map((printer) => (
                                                            <TableRow
                                                                key={printer.address}
                                                                hover={true}
                                                            >
                                                                <TableCell>
                                                                    <Box display="flex" alignItems="center">
                                                                        <Typography variant="body2">
                                                                            {printer.address}
                                                                        </Typography>
                                                                        {printer.isMain && (
                                                                            <Box ml={1}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={i18n.t('common.isMain').toUpperCase()}
                                                                                    color="primary"
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {printer.model}
                                                                </TableCell>
                                                                <TableCell width={140}>
                                                                    <Box display="flex" alignItems="center">
                                                                        {i18n.t(`printerInfoStatus.${PrinterInfoStatus[printer.printerStatus]?.toLowerCase()}`)}
                                                                        {
                                                                            printer.printerStatus === PrinterInfoStatus.OutOfPaper &&
                                                                            <img alt="warning" src={images.icons.warning} className={classes.mLeft5}/>
                                                                        }
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {printer.workAreas.map(x => x.name).join(", ")}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <BasicThreeDotsMenu
                                                                        items={preparePrinterMenuItems(server, printer)}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={dialogOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                disableEscapeKeyDown
                fullWidth={true}
            >
                <DialogTitle classes={{ root: classes.titleRoot }} id="alert-dialog-title">
                    {dialogTitle}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText className={classes.dialogContentText}>
                        <ol>
                            <li>
                                {i18n.t('warnings.printerServerSaveFileSteps.step1')}&nbsp;
                                <b>{i18n.t('warnings.printerServerSaveFileSteps.filename')}</b>
                            </li>
                            <li>{i18n.t('warnings.printerServerSaveFileSteps.step2')}</li>
                            <li>{i18n.t('warnings.printerServerSaveFileSteps.step3')}</li>
                            <li>{i18n.t('warnings.printerServerSaveFileSteps.step4')}</li>
                            <li>{i18n.t('warnings.printerServerSaveFileSteps.step5')}</li>
                        </ol>
                    </DialogContentText>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button variant="contained" color="primary" onClick={handleCopyAndClose}>
                        {i18n.t('button.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <PopupConfirmation
                open={confirmDialogOpen && selectedServer !== null}
                close={() => {
                    setSelectedServer(null);
                    setConfirmDialogOpen(false);
                }}
                basicButton={true}
                activeBtn={i18n.t('button.confirm')}
                description={i18n.t('confirmation.generateNewApiKeyText')}
                title={i18n.t('common.newApiKeyTitle')}
                action={() => handleRegenerateKey(selectedServer!)}
            />
        </>
    );
}
